import { PitayaClient, setPitayaClient, getPitayaClient } from './PitayaClient';
import { useAuthStore } from '@/state/authStore';
import { useUIStore } from '@/state/uiStore';
import { attachNotificationListeners, detachNotificationListeners } from '@/state/notificationStore';

const WS = import.meta.env.VITE_WS_URL || 'ws://localhost:3250/ws';

// Auto-reconnect config
const MAX_RECONNECT_ATTEMPTS = 10;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;

let reconnectAttempts = 0;
let reconnectTimer: number | undefined;
let manualDisconnect = false;
let kicked = false;
let reconnecting = false;

function clearReconnectTimer() {
  if (reconnectTimer !== undefined) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = undefined;
  }
}

function scheduleReconnect(code: number) {
  if (manualDisconnect || kicked) return;
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    useUIStore.getState().showToast('Không thể kết nối lại server. Vui lòng tải lại trang.', 'error');
    return;
  }
  // Don't try to reconnect without a token
  const token = useAuthStore.getState().accessToken;
  if (!token) return;

  const delay = Math.min(BASE_DELAY_MS * Math.pow(2, reconnectAttempts), MAX_DELAY_MS);
  reconnectAttempts++;
  useUIStore.getState().showToast(
    `Mất kết nối server (${code}). Đang thử lại lần ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}...`,
    'warn',
  );
  clearReconnectTimer();
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = undefined;
    setPitayaClient(null);
    ensureRealtime().catch(() => {
      // onClose handler will reschedule if needed
    });
  }, delay);
}

export async function ensureRealtime(): Promise<PitayaClient> {
  const existing = getPitayaClient();
  if (existing && existing.isConnected()) return existing;
  if (reconnecting && existing) {
    // A connection attempt is already in-flight; wait briefly and return existing if it becomes ready.
  }

  manualDisconnect = false;
  kicked = false;
  reconnecting = true;

  const token = useAuthStore.getState().accessToken ?? undefined;
  const cli = new PitayaClient({
    url: WS,
    token,
    onOpen: () => {
      if (reconnectAttempts > 0) {
        useUIStore.getState().showToast('Đã kết nối lại server', 'info');
      }
      reconnectAttempts = 0;
      clearReconnectTimer();
      attachNotificationListeners();
    },
    onClose: (code) => {
      reconnecting = false;
      if (manualDisconnect || kicked) {
        useUIStore.getState().showToast(`Mất kết nối server (${code})`, 'warn');
        return;
      }
      scheduleReconnect(code);
    },
    onKick: () => {
      kicked = true;
      clearReconnectTimer();
      useUIStore.getState().showToast('Bạn đã bị kick khỏi server', 'error');
      useAuthStore.getState().clear();
    },
  });
  setPitayaClient(cli);
  try {
    await cli.connect();
    reconnecting = false;
    return cli;
  } catch (e) {
    reconnecting = false;
    throw e;
  }
}

export function disconnectRealtime() {
  manualDisconnect = true;
  clearReconnectTimer();
  reconnectAttempts = 0;
  detachNotificationListeners();
  getPitayaClient()?.close();
  setPitayaClient(null);
}
